import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EmergencyIncident } from '@src/models/interfaces/emergency.interface';

const MUTE_STORAGE_KEY = 'guarnic.emergencies.alarmMuted';
const ALARM_REPEAT_MS = 4000;
const TITLE_FLASH_MS = 1000;

type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

interface UseEmergencyAlertsOptions {
  incidents: EmergencyIncident[];
  /**
   * Must be false until the incident subscription has delivered at least one
   * snapshot. Enabling earlier makes the hook adopt an empty baseline and then
   * alarm for every incident that already existed.
   */
  enabled: boolean;
  /**
   * Identity of the current incident scope (organization + divisions). When it
   * changes the baseline is re-adopted silently, so switching scope does not
   * alarm for every incident in the new one.
   */
  resetKey: string;
  /** Localized strings; passing them in keeps this hook free of react-intl. */
  messages: {
    notificationTitle: string;
    notificationBody: (incident: EmergencyIncident) => string;
    titleFlash: (count: number) => string;
  };
  onOpenIncident?: (incidentId: string) => void;
}

interface EmergencyAlertsState {
  muted: boolean;
  toggleMute: () => void;
  /** True when the browser blocked audio until the operator interacts with the page. */
  audioBlocked: boolean;
  enableAudio: () => void;
  notificationPermission: NotificationPermissionState;
  requestNotificationPermission: () => void;
  /** Incidents that arrived since the operator last acknowledged. */
  unacknowledgedIds: string[];
  acknowledge: () => void;
}

const readStoredMute = (): boolean => {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  } catch {
    // Private mode or storage disabled: default to audible, which is the safe
    // failure direction for an emergency console.
    return false;
  }
};

/**
 * Plays a short two-tone alarm through the Web Audio API.
 *
 * Synthesised rather than shipped as an asset so there is no extra network
 * request at the moment an alarm needs to fire, and no binary in the bundle.
 */
const playAlarmTone = (context: AudioContext): void => {
  const now = context.currentTime;
  const gain = context.createGain();
  gain.connect(context.destination);
  gain.gain.setValueAtTime(0.0001, now);

  [880, 660].forEach((frequency, index) => {
    const startAt = now + index * 0.28;
    const endAt = startAt + 0.24;
    const oscillator = context.createOscillator();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(frequency, startAt);
    oscillator.connect(gain);
    // Ramp instead of hard gating so the tone does not click on cheap speakers.
    gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt);
    oscillator.start(startAt);
    oscillator.stop(endAt + 0.02);
  });
};

const getNotificationPermission = (): NotificationPermissionState => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as NotificationPermissionState;
};

/**
 * Raises the alarm when a new incident appears while the console is open:
 * repeating audible tone, desktop notification, and a flashing tab title.
 *
 * Incidents present on the first load never alarm — only genuinely new ones do,
 * otherwise every page refresh would sound the alarm and operators would learn
 * to ignore it.
 */
export const useEmergencyAlerts = ({
  incidents,
  enabled,
  resetKey,
  messages,
  onOpenIncident,
}: UseEmergencyAlertsOptions): EmergencyAlertsState => {
  const [muted, setMuted] = useState(readStoredMute);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermissionState>(getNotificationPermission);
  const [unacknowledgedIds, setUnacknowledgedIds] = useState<string[]>([]);

  const knownIncidentIdsRef = useRef<Set<string> | null>(null);
  const baselineScopeRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const baseTitleRef = useRef<string>('');
  const onOpenIncidentRef = useRef(onOpenIncident);
  const messagesRef = useRef(messages);

  onOpenIncidentRef.current = onOpenIncident;
  messagesRef.current = messages;

  // -- Detect newly arrived incidents ---------------------------------------
  useEffect(() => {
    if (!enabled) return;
    const currentIds = new Set(incidents.map(incident => incident.id));

    if (knownIncidentIdsRef.current === null || baselineScopeRef.current !== resetKey) {
      // First delivery for this scope: adopt as the baseline silently. Also
      // covers switching organization or division, where the whole incident
      // set legitimately changes and none of it is new.
      knownIncidentIdsRef.current = currentIds;
      baselineScopeRef.current = resetKey;
      setUnacknowledgedIds([]);
      return;
    }

    const known = knownIncidentIdsRef.current;
    const arrived = incidents.filter(incident => !known.has(incident.id));
    knownIncidentIdsRef.current = currentIds;

    if (!arrived.length) {
      // Drop acknowledgements for incidents that have since been resolved so a
      // later incident with a recycled position cannot inherit them.
      setUnacknowledgedIds(current => current.filter(id => currentIds.has(id)));
      return;
    }

    setUnacknowledgedIds(current => [
      ...current.filter(id => currentIds.has(id)),
      ...arrived.map(incident => incident.id),
    ]);

    if (notificationPermission === 'granted') {
      arrived.forEach(incident => {
        try {
          const notification = new Notification(messagesRef.current.notificationTitle, {
            body: messagesRef.current.notificationBody(incident),
            tag: `emergency-${incident.id}`,
            requireInteraction: true,
          });
          notification.onclick = () => {
            window.focus();
            onOpenIncidentRef.current?.(incident.id);
            notification.close();
          };
        } catch {
          // Notification construction can throw on some mobile browsers that
          // expose the API but only allow it from a service worker. The
          // in-app alarm still covers the operator.
        }
      });
    }
  }, [enabled, incidents, notificationPermission, resetKey]);

  // -- Repeating audible alarm ----------------------------------------------
  useEffect(() => {
    if (!enabled || muted || !unacknowledgedIds.length) return undefined;
    if (typeof window === 'undefined') return undefined;

    const AudioContextConstructor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return undefined;

    if (audioContextRef.current === null) {
      audioContextRef.current = new AudioContextConstructor();
    }
    const context = audioContextRef.current;

    let cancelled = false;
    const sound = () => {
      if (cancelled) return;
      if (context.state === 'suspended') {
        // Autoplay policy: needs a user gesture before it will produce sound.
        setAudioBlocked(true);
        void context.resume().then(() => {
          if (!cancelled && context.state === 'running') setAudioBlocked(false);
        });
        return;
      }
      setAudioBlocked(false);
      playAlarmTone(context);
    };

    sound();
    const interval = window.setInterval(sound, ALARM_REPEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled, muted, unacknowledgedIds.length]);

  useEffect(
    () => () => {
      void audioContextRef.current?.close();
      audioContextRef.current = null;
    },
    [],
  );

  // -- Tab title flash -------------------------------------------------------
  useEffect(() => {
    if (!enabled || !unacknowledgedIds.length) return undefined;
    if (typeof document === 'undefined') return undefined;

    if (!baseTitleRef.current) baseTitleRef.current = document.title;
    const baseTitle = baseTitleRef.current;
    let showAlert = true;

    const restore = () => {
      document.title = baseTitle;
    };

    const flash = () => {
      // Only compete for the tab title while the operator is looking elsewhere.
      if (document.visibilityState === 'visible') {
        restore();
        return;
      }
      document.title = showAlert
        ? messagesRef.current.titleFlash(unacknowledgedIds.length)
        : baseTitle;
      showAlert = !showAlert;
    };

    flash();
    const interval = window.setInterval(flash, TITLE_FLASH_MS);
    document.addEventListener('visibilitychange', flash);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', flash);
      restore();
    };
  }, [enabled, unacknowledgedIds]);

  const toggleMute = useCallback(() => {
    setMuted(current => {
      const next = !current;
      try {
        window.localStorage.setItem(MUTE_STORAGE_KEY, String(next));
      } catch {
        // Preference simply will not persist; muting still applies this session.
      }
      return next;
    });
  }, []);

  const enableAudio = useCallback(() => {
    const context = audioContextRef.current;
    if (!context) {
      setAudioBlocked(false);
      return;
    }
    void context.resume().then(() => setAudioBlocked(context.state !== 'running'));
  }, []);

  const requestNotificationPermission = useCallback(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    void Notification.requestPermission().then(permission =>
      setNotificationPermission(permission as NotificationPermissionState),
    );
  }, []);

  const acknowledge = useCallback(() => setUnacknowledgedIds([]), []);

  return useMemo(
    () => ({
      muted,
      toggleMute,
      audioBlocked,
      enableAudio,
      notificationPermission,
      requestNotificationPermission,
      unacknowledgedIds,
      acknowledge,
    }),
    [
      acknowledge,
      audioBlocked,
      enableAudio,
      muted,
      notificationPermission,
      requestNotificationPermission,
      toggleMute,
      unacknowledgedIds,
    ],
  );
};
